import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/db7ndoitp9sllopf7i4g";

const CONTACTS = [
  { icon: "ri-mail-line", label: "邮箱", value: "support@freeonlinemovie.demo" },
  { icon: "ri-wechat-line", label: "微信", value: "FreeMovie_Official" },
  { icon: "ri-weibo-line", label: "微博", value: "@免费在线电影" },
  { icon: "ri-customer-service-2-line", label: "客服", value: "400-888-0000（工作日 9:00-18:00）" },
];

export default function ContactPage() {
  useSeo({
    title: "联系我们 - 免费在线电影",
    description: "通过邮箱、微信或意见反馈表单联系免费在线电影团队，欢迎提出建议、内容合作或版权反馈，我们会尽快回复您。",
    keywords: "联系我们,免费在线电影,意见反馈,联系方式,内容合作",
  });

  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast("已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    // honeypot check - silently accept but do not send
    const honey = String(payload.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setValues({ name: "", email: "", message: "" });
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "website_alt") return;
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setValues({ name: "", email: "", message: "" });
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "网络异常，请稍后重试");
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-100/80" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">联系我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm text-background-100/80 md:text-base">
            有任何建议、合作意向或版权反馈，欢迎随时与我们联系。
          </p>
        </div>
      </div>

      <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-12 md:px-6 lg:grid-cols-[380px_1fr]">
        {/* Contact info */}
        <aside>
          <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
          <p className="mt-2 text-sm text-foreground-600">点击任意条目即可复制内容。</p>
          <div className="mt-5 flex flex-col gap-3">
            {CONTACTS.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => handleCopy(item.value)}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-accent-300"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  <p className="truncate text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
                <i className="ri-file-copy-line ml-auto text-foreground-400 transition-colors group-hover:text-accent-500"></i>
              </button>
            ))}
          </div>

          <div className="mt-5 rounded-lg bg-secondary-100 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-secondary-900">
              <i className="ri-time-line"></i>
              回复时间
            </div>
            <p className="mt-1 text-sm text-secondary-800">我们会在 1-2 个工作日内回复您的留言，感谢理解与支持。</p>
          </div>
        </aside>

        {/* Feedback form */}
        <section>
          <h2 className="font-heading text-xl font-bold text-foreground-950">意见反馈</h2>
          <p className="mt-2 text-sm text-foreground-600">填写下面的表单，把您的想法告诉我们。</p>

          <form
            data-readdy-form
            id="contact-feedback-form"
            onSubmit={handleSubmit}
            className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground-800">姓名</span>
                <input
                  type="text"
                  name="name"
                  required
                  value={values.name}
                  onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
                  placeholder="请输入您的姓名"
                  className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-accent-400"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground-800">邮箱</span>
                <input
                  type="email"
                  name="email"
                  required
                  value={values.email}
                  onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
                  placeholder="请输入您的邮箱"
                  className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-accent-400"
                />
              </label>
            </div>

            <label className="mt-4 flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground-800">留言内容</span>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                value={values.message}
                onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
                placeholder="请输入您的意见或建议（最多 500 字）"
                className="resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-accent-400"
              />
              <span className="self-end text-xs text-foreground-400">{values.message.length}/500</span>
            </label>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-guard-aux"
            />

            {status === "error" && formError && (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 p-3 text-sm text-accent-800">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{formError}</span>
              </div>
            )}

            {status === "success" && (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 p-3 text-sm text-primary-800">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>感谢您的反馈！我们已收到您的留言，会尽快与您联系。</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-5 flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
            >
              <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
              {status === "submitting" ? "提交中..." : "提交反馈"}
            </button>
          </form>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-in rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}